import React, { useEffect, useMemo, useRef, useState } from 'react';
import FlyerPlaceholder from './FlyerPlaceholder';

const defaultEmptyLabelStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '100%',
  padding: '0.75rem',
  textAlign: 'center',
  fontSize: '0.82rem',
  lineHeight: 1.45,
  color: 'var(--slate-400)',
};

const DEFAULT_IMAGE_RETRY_DELAYS = [450, 1200, 2400];

const shouldAppendRetryParam = (url) => {
  if (!url || typeof url !== 'string') return false;
  return !url.startsWith('blob:') && !url.startsWith('data:');
};

const appendRetryParam = (url, attempt) => {
  if (!shouldAppendRetryParam(url) || !attempt) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}flyer_retry=${attempt}`;
};

const splitHash = (url) => {
  const [withoutHash, hash = ''] = String(url || '').split('#');
  return { withoutHash, hash: hash ? `#${hash}` : '' };
};

const readQueryParam = (url, key) => {
  const { withoutHash } = splitHash(url);
  const queryIndex = withoutHash.indexOf('?');
  if (queryIndex === -1) return '';
  const params = new URLSearchParams(withoutHash.slice(queryIndex + 1));
  return params.get(key) || '';
};

const setQueryParam = (url, key, value) => {
  if (!url || !key) return url;
  const { withoutHash, hash } = splitHash(url);
  const queryIndex = withoutHash.indexOf('?');
  const path = queryIndex === -1 ? withoutHash : withoutHash.slice(0, queryIndex);
  const params = new URLSearchParams(queryIndex === -1 ? '' : withoutHash.slice(queryIndex + 1));
  if (value === null || value === undefined || value === '') {
    params.delete(key);
  } else {
    params.set(key, value);
  }
  const query = params.toString();
  return `${path}${query ? `?${query}` : ''}${hash}`;
};

const buildImageUrlCandidates = (url) => {
  const raw = String(url || '').trim();
  if (!raw) return [];
  const candidates = [raw];
  const variant = readQueryParam(raw, 'variant').toLowerCase();
  if (variant === 'card' || variant === 'web' || variant === 'detail') {
    candidates.push(setQueryParam(raw, 'variant', 'thumb'));
    candidates.push(setQueryParam(raw, 'variant', ''));
  } else if (variant === 'thumb' || variant === 'tiny' || variant === 'list') {
    candidates.push(setQueryParam(raw, 'variant', ''));
  } else if (variant === 'proxy') {
    candidates.push(setQueryParam(raw, 'variant', 'card'));
    candidates.push(setQueryParam(raw, 'variant', 'thumb'));
    candidates.push(setQueryParam(raw, 'variant', ''));
  }
  return Array.from(new Set(candidates.filter(Boolean)));
};

export default function EventFlyerMedia({
  event = null,
  imageUrl = '',
  imageAlt = 'フライヤー',
  sizeVariant = 'large',
  aspectRatio = '1 / 1.414',
  borderRadius = 12,
  className = '',
  style = {},
  placeholderClassName = '',
  placeholderStyle = {},
  imageClassName = '',
  imageStyle = {},
  hasImage,
  visualMode = 'decorated',
  placeholderDesignKey = '',
  placeholderColorKey = '',
  showPlaceholder = true,
  emptyContent = null,
  loading = 'lazy',
  fetchPriority,
  onImageLoad,
  onImageError,
  onImageClick,
  onDisplayTimeout,
  displayTimeoutMs = 0,
  autoRetry = true,
  maxImageRetries = 3,
  retryDelays = DEFAULT_IMAGE_RETRY_DELAYS,
}) {
  const [retryAttempt, setRetryAttempt] = useState(0);
  const [candidateIndex, setCandidateIndex] = useState(0);
  const [isRetryWaiting, setIsRetryWaiting] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const retryTimerRef = useRef(null);
  const displayTimerRef = useRef(null);
  const displayTimeoutFiredRef = useRef(false);
  const onDisplayTimeoutRef = useRef(onDisplayTimeout);
  const safeMaxRetries = Math.max(0, Number(maxImageRetries) || 0);
  const normalizedRetryDelays = Array.isArray(retryDelays) && retryDelays.length
    ? retryDelays
    : DEFAULT_IMAGE_RETRY_DELAYS;
  const imageCandidates = useMemo(() => buildImageUrlCandidates(imageUrl), [imageUrl]);
  const activeImageUrl = imageCandidates[candidateIndex] || imageCandidates[0] || imageUrl;
  const resolvedImageUrl = useMemo(
    () => appendRetryParam(activeImageUrl, retryAttempt),
    [activeImageUrl, retryAttempt]
  );
  const resolvedHasImage = typeof hasImage === 'boolean'
    ? hasImage && !imageFailed
    : Boolean(imageUrl) && !imageFailed;
  const shouldShowEmptyState = !imageUrl && !showPlaceholder;
  const shouldRenderImage = Boolean(imageUrl) && !imageFailed && !isRetryWaiting;

  onDisplayTimeoutRef.current = onDisplayTimeout;

  const clearDisplayTimer = () => {
    if (displayTimerRef.current) {
      clearTimeout(displayTimerRef.current);
      displayTimerRef.current = null;
    }
  };

  useEffect(() => {
    setRetryAttempt(0);
    setCandidateIndex(0);
    setIsRetryWaiting(false);
    setImageFailed(false);
    setImageLoaded(false);
    displayTimeoutFiredRef.current = false;
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, [imageUrl]);

  useEffect(() => {
    clearDisplayTimer();
    displayTimeoutFiredRef.current = false;
    const timeout = Number(displayTimeoutMs) || 0;
    if (!imageUrl || timeout <= 0) return undefined;
    displayTimerRef.current = setTimeout(() => {
      displayTimerRef.current = null;
      if (displayTimeoutFiredRef.current) return;
      displayTimeoutFiredRef.current = true;
      onDisplayTimeoutRef.current?.();
    }, timeout);
    return clearDisplayTimer;
  }, [displayTimeoutMs, imageUrl]);

  useEffect(() => () => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    clearDisplayTimer();
  }, []);

  const scheduleImageRetry = (target, event) => {
    const hasNextCandidate = candidateIndex < imageCandidates.length - 1;
    const canRetry = Boolean(autoRetry && imageUrl && (retryAttempt < safeMaxRetries || hasNextCandidate));
    if (!canRetry) {
      setImageFailed(true);
      setIsRetryWaiting(false);
      clearDisplayTimer();
      onImageError?.(target, event);
      return;
    }

    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }

    const delay = normalizedRetryDelays[Math.min(retryAttempt, normalizedRetryDelays.length - 1)] || 1000;
    setImageLoaded(false);
    setIsRetryWaiting(true);
    retryTimerRef.current = setTimeout(() => {
      retryTimerRef.current = null;
      if (hasNextCandidate) {
        setCandidateIndex((current) => Math.min(current + 1, imageCandidates.length - 1));
        setRetryAttempt(0);
      } else {
        setRetryAttempt((current) => current + 1);
      }
      setIsRetryWaiting(false);
    }, delay);
  };

  const handleImageLoad = (event) => {
    const target = event.currentTarget;
    if (!target?.naturalWidth || !target?.naturalHeight) {
      scheduleImageRetry(target, event);
      return;
    }
    setImageFailed(false);
    setImageLoaded(true);
    setIsRetryWaiting(false);
    clearDisplayTimer();
    onImageLoad?.(target, event);
  };

  const handleImageError = (event) => {
    scheduleImageRetry(event.currentTarget, event);
  };

  return (
    <div
      className={className}
      style={{
        position: 'relative',
        width: '100%',
        ...(shouldShowEmptyState
          ? {
              aspectRatio,
              borderRadius,
              overflow: 'hidden',
              background: 'var(--slate-50)',
              border: '1px dashed var(--slate-300)',
            }
          : null),
        ...style,
      }}
    >
      {showPlaceholder ? (
        <FlyerPlaceholder
          width="100%"
          aspectRatio={aspectRatio}
          borderRadius={borderRadius}
          className={placeholderClassName}
          style={placeholderStyle}
          event={event}
          designKey={placeholderDesignKey}
          colorKey={placeholderColorKey}
          sizeVariant={sizeVariant}
          hasImage={resolvedHasImage && imageLoaded}
          visualMode={visualMode}
        />
      ) : null}

      {shouldRenderImage ? (
        <img
          key={`${resolvedImageUrl}:${retryAttempt}`}
          src={resolvedImageUrl}
          alt={imageAlt}
          className={`${imageClassName || ''} event-flyer-image--fade${imageLoaded ? ' is-visible' : ''}`.trim()}
          style={{
            ...(showPlaceholder
              ? {
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                }
              : null),
            ...imageStyle,
          }}
          loading={loading}
          fetchpriority={fetchPriority || undefined}
          decoding="async"
          onLoad={handleImageLoad}
          onError={handleImageError}
          onClick={(event) => onImageClick?.(event.currentTarget, event)}
        />
      ) : null}

      {shouldShowEmptyState
        ? (emptyContent || <div style={defaultEmptyLabelStyle}>フライヤー未登録</div>)
        : null}
    </div>
  );
}
